import debug from "../debug";

/**
 * Set tooltip
 *
 * @param tooltip {HTMLElement}
 * @param fieldState {Object|String|Boolean}
 */
const setTooltip = (tooltip, fieldState) => {
  const heading = tooltip.querySelector(".fe__tooltip_heading");
  const format = tooltip.querySelector(".fe__tooltip_format");
  const hideFormat = "string" === typeof (fieldState) ||
    ("object" === typeof (fieldState) && "undefined" === typeof (fieldState.format));
  const tooltipTxt = "object" === typeof (fieldState) && "undefined" !== typeof (fieldState.heading) ?
    fieldState.heading :
    fieldState;

  if (hideFormat) {
    format.classList.add("norender");
  } else {
    format.classList.remove("norender");
    format.innerHTML = fieldState.format;
  }
  heading.innerHTML = tooltipTxt;
};

/**
 * Show tooltip
 *
 * @param field {HTMLFormElement}
 * @param fieldState {Object|Boolean}
 */
const showTooltip = (field, fieldState = null) => {
  const fieldStat = null === fieldState ?
    ("undefined" !== typeof (field.fieldState) ? field.fieldState : true) :
    fieldState;
  // Cowardly refuse to show tooltip for a valid field
  if (fieldHasValue(field) && true === fieldStat) {
    return;
  }

  const tooltip = field.parentNode.querySelector(".fe__tooltip");
  if (!fieldHasValue(field)) {
    // Means required
    setTooltip(tooltip, "Required");
  } else {
    setTooltip(tooltip, fieldStat);
  }

  // Show tooltip
  tooltip.classList.remove("fe__tooltip_hidden");
};

/**
 * Hide tooltip
 *
 * @param field {HTMLFormElement}
 */
const hideTooltip = field => {
  field.parentNode.querySelector(".fe__tooltip").classList.add("fe__tooltip_hidden");
};

/**
 * Does a filed has a value?
 *
 * @param field {HTMLFormElement}
 *
 * @return {Boolean}
 */
const fieldHasValue = field => {
  switch (field.nodeName.toLowerCase()) {
    case "input":
      return field.value.length > 0;

    case "select":
      return field.selectedIndex > 0;
  }

  return false;
};

/**
 * Get a field state
 *
 * @param field {HTMLFormElement}
 * @param validateOpts {Object}
 *
 * @return {Object|Boolean} TRUE if valid, otherwise the text with the failure
 */
const getFieldState = (field, validateOpts) => {
  let label;
  label = (label = field.getAttribute("aria-label")) ? label : "Input";

  // Not required, always valid
  if (!validateOpts.required) {
    return true;
  }

  if (!fieldHasValue(field)) {
    if (!validateOpts.guidance || !validateOpts.guidance["ERR_MISSING"]) {
      // Required, but no guidance, or no contextually relevant guidance
      return `Valid ${label} Required`;
    }

    // If we're here, it's missing, and there's specific guidance for that case
    return validateOpts.guidance["ERR_MISSING"].replace("{%fieldName%}", label);
  }

  let validateResult;
  const temp = field.value.trim();
  // Expected to return false .function when field valid
  const malformatted = "undefined" !== typeof (validateOpts.re) && null === temp.match(validateOpts.re) ?
    "ERR_MALFORMATTED" :
    ("function" === typeof (validateOpts.function) && true !== (validateResult = validateOpts.function({value: temp, field})) ?
      validateResult :
      false);

  if (malformatted) {
    if (!validateOpts.guidance || !validateOpts.guidance[malformatted]) {
      // Malformatted, but no specific guidance
      return `Please Review ${label}`;
    }

    // If we're here, it's malformatted, and there's specific guidance for that case
    const guidance = {
      heading: (validateOpts.guidance[malformatted].heading ?
        validateOpts.guidance[malformatted].heading :
        validateOpts.guidance[malformatted]).replace("{%fieldName%}", label),
    };
    if (validateOpts.guidance[malformatted].format) {
      guidance.format = validateOpts.guidance[malformatted].format.replace("{%fieldName%}", label);
    }

    return guidance;
  }

  return true;
};

/**
 * Verifies field content compliance with a regexp/function defined in options
 *
 * @param target {HTMLElement}
 * @param options {Object}
 *
 * @return {Boolean}
 */
const validateField = (target, options) => {
  // If autocomplete loading list, or disabled, or has not focused the field for the first time
  /*if (target.classList.contains("fe__autocomplete_input") &&
    (!target.dataset.listLoaded || target.disabled || !target.dataset.enteredField)) {
    return true;
  }*/

  // Assume the worst and record the assumption
  const fieldState = getFieldState(target, options);
  target.fieldState = fieldState;

  // Get field status indicator for the field
  const fieldWrapper = target.parentNode;
  const fsi = fieldWrapper.querySelector(".fe__status_indicator_wrapper");

  // If options defines a form, should also be verified
  if (options.formId) {
    validateForm(options.formId);
  }

  if (fsi.classList.contains("fe__required")) {
    if (true === fieldState) {
      // Hide required icon
      fsi.querySelector(".icon__required").classList.add("norender");

      // Hide error icon
      fsi.querySelector(".icon__error").classList.add("norender");

      // Show complete icon
      fsi.querySelector(".icon__complete").classList.remove("norender");

      // Hide tooltip
      hideTooltip(target);

      return true;
    } else {
      // Show required icon
      fsi.querySelector(".icon__required").classList.remove("norender");

      // Hide error icon
      fsi.querySelector(".icon__error").classList.add("norender");

      // Hide complete icon
      fsi.querySelector(".icon__complete").classList.add("norender");

      // Show tooltip
      showTooltip(target, fieldState);

      return false;
    }
  }

  return true;
};

/**
 * Iterate and inspect a validate_fields ds; return true if *all* fields are set to true
 *
 * @param form {HTMLFormElement} The Form
 * @param fieldNames {String[]} The field to iterate
 *
 * @return {Boolean}
 */
const inspectFields = (form, fieldNames) => {
  // Assume success
  let fieldsValid = true, field;

  // Iterate until list end or until a field marked false is encountered
  for (let i = 0; i < fieldNames.length && fieldsValid; i++) {
    field = form.querySelector(`#${fieldNames[i]}`);
    if (!field) {
      debug(["🐛 %cforms ~ validation ~ inspectFields", "font-weight:bold"],
        `Field not found in form. ${fieldNames[i]}`, form);

      return false;
    }
    fieldsValid = true === field.fieldState;
  }

  return fieldsValid;
};

/**
 * Enable form control
 *
 * @param control {HTMLElement}
 */
const enableFormControl = control => {
  if (!control) {
    debug(["🐛 %cforms ~ validation ~ enableFormControl", "font-weight:bold"], "Unable to get form control");

    return;
  }

  control.classList.remove("fe__button_disabled");
};

/**
 * Disable form control
 *
 * @param control {HTMLElement}
 */
const disableFormControl = control => {
  if (!control) {
    debug(["🐛 %cforms ~ validation ~ disableFormControl", "font-weight:bold"], "Unable to get form control");

    return;
  }

  control.classList.add("fe__button_disabled");
};

/**
 * Verifies that a full set of fields have been individually
 * validated (via input driven calls to validateField)
 *
 * @param fId {String|Array} The form(s) to validate
 *
 * @return {Boolean}
 */
const validateForm = fId => {
  // Upstream implementations may forget to pass an array, but this method
  // iterates an array, so if we get a string, array-ify it
  const formIds = "string" === typeof (fId) ? [fId] : fId;
  let formValid = true, form;

  // fId may contain validation instructions for from more than one form
  // iterate the information provided
  for (let i = 0; i < formIds.length && formValid; i++) {
    form = document.querySelector(`form#${formIds[i]}`);
    if (!form) {
      debug(["🐛 %cforms ~ validation ~ validateForm", "font-weight:bold"],
        `Unable to find form with selector form#${formIds[i]}`);

      formValid = false;
    }
    if (!form.fieldsValidators) {
      debug(["🐛 %cforms ~ validation ~ validateForm", "font-weight:bold"],
        `Unable to get field validators for form with selector form#${formIds[i]}`, form);

      formValid = false;
    }

    formValid = inspectFields(form, Object.keys(form.fieldsValidators));
    if (!formValid) {
      disableFormControl(form.formControl);
    } else {
      enableFormControl(form.formControl);
    }
  }

  return formValid;
};

export {validateField, validateForm, showTooltip, hideTooltip};
export default validateField;
